import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import DetailPageHeader from '../../detail_page_header/DetailPageHeader';
import { nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import {
	troubleshootingGuidesRestCrudThunksForBuyer,
	troubleshootingGuidesRestCrudThunksForSupplier,
} from '../../../thunks/troubleshooting_guides_thunks';
import { withUserType } from '../../withUserType';
import { Button, message, Popconfirm } from 'antd';
import TroubleshootingGuideNewOrEditModal from '../TroubleshootingGuideNewOrEditModal';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { areStepsAvailable } from '../../../utils/TroubleshootingUtils';
import DateTimeHover from '../../date_time_component/DateTime';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../../utils/AuthUtils';

const TroubleshootingDetailPage: FC<any> = ({
	companyConfig,
	fetching,
	childRoutes,
	history,
	match,
	getTroubleshootingGuide,
	userType,
	troubleshootingGuide,
	deleteGuide,
}): React.ReactElement => {
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const backLink = useMemo(() => `/${userType}/workflows/troubleshooting`, [userType]);

	const tabs = useMemo(
		() => [
			{
				key: 'steps',
				name: 'Steps',
			},
			{
				key: 'associations',
				name: 'Associations',
			},
		],
		[]
	);

	const tsId = useMemo(() => match.params.id, [match.params.id]);

	useEffect(() => {
		tsId && getTroubleshootingGuide(tsId);
	}, [tsId, getTroubleshootingGuide]);

	const basePath = useMemo(() => `/${userType}/workflows/troubleshooting/detail`, [userType]);

	const handleTabChange = useCallback(
		(key) => history.push(`${basePath}/${tsId}/${key}`),
		[basePath, history, tsId]
	);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1;
		},
		[history.location.pathname]
	);

	const title = useMemo(
		() => nullSafeGetOrElse('name', troubleshootingGuide, 'Troubleshooting Guide'),
		[troubleshootingGuide]
	);

	const _onEdit = () => setEditModalVisible(true);
	const _onEditCancel = () => setEditModalVisible(false);

	const onDelete = useCallback(() => {
		setDeleting(true);
		deleteGuide(troubleshootingGuide)
			.then(() => {
				message.success('Guide deleted successfully!');
				history.replace(backLink);
			})
			.catch(message.error)
			.finally(() => setDeleting(false));
	}, [backLink, deleteGuide, history, troubleshootingGuide]);

	const publishedVersionAvailable = useMemo(
		() => areStepsAvailable('troubleshootingSteps', troubleshootingGuide),
		[troubleshootingGuide]
	);

	const draftAvailable = useMemo(
		() => areStepsAvailable('draftTroubleshootingSteps', troubleshootingGuide),
		[troubleshootingGuide]
	);

	const guideStatusText = useMemo(() => {
		const status = draftAvailable ? 'draft' : publishedVersionAvailable ? 'published' : null;
		const updatedTime = nullSafeGet('updatedAt', troubleshootingGuide);
		const parsedTime = <DateTimeHover timestamp={updatedTime} />;

		if (status) {
			return (
				<div className="flex flex-row items-center">
					<span
						className={`
							mr-2 inline-block h-2.5 w-2.5 rounded-full
							bg-gray-200 ${status === 'draft' ? 'bg-yellow-500' : ''}
							${status === 'published' ? 'bg-green-500' : ''}
						`}
					/>
					<div className="ml-1">{status === 'draft' ? 'Draft' : ''}</div>
					<div className="ml-1">{status === 'published' ? 'Published' : ''}</div>
					{updatedTime && <div className="ml-2">Last Updated {parsedTime}</div>}
				</div>
			);
		} else {
			return null;
		}
	}, [draftAvailable, publishedVersionAvailable, troubleshootingGuide]);

	return (
		<>
			<DetailPageHeader
				exactPath={`${basePath}/:id`}
				redirectPath={`${basePath}/${tsId}/steps`}
				backLinkText={'Back to Guide'}
				backLinkTo={backLink}
				title={
					fetching ? null : (
						<div className="flex flex-col gap-2">
							{title}
							<div className="text-sm">{guideStatusText}</div>
						</div>
					)
				}
				subtitle={fetching ? null : nullSafeGetOrElse('description', troubleshootingGuide, '')}
				actions={
					<div className="flex flex-row items-center gap-2">
						<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING}>
							<Button key={1} ghost={true} icon={<EditOutlined />} onClick={_onEdit}>
								Edit
							</Button>
						</AccessPermissionChecker>
						<AccessPermissionChecker name={PERMISSION_NAMES.DELETE_TROUBLESHSHOOTING}>
							<Popconfirm
								title="You won't be recover this steps once deleting. Are you sure you want to delete this guide?"
								onConfirm={onDelete}
								okText="Yes, Delete"
								cancelText="Cancel"
							>
								<Button key={2} ghost={true} icon={<DeleteOutlined />} loading={deleting}>
									Delete
								</Button>
							</Popconfirm>
						</AccessPermissionChecker>
					</div>
				}
				tabs={tabs}
				backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				handleTabChange={handleTabChange}
				checkTabActive={checkTabActive}
				childRoutes={childRoutes}
				childBodyBackgroudColor={'#FFFFFF'}
			/>
			{editModalVisible ? (
				<TroubleshootingGuideNewOrEditModal
					onSuccess={_onEditCancel}
					onCancel={_onEditCancel}
					formData={troubleshootingGuide}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	companyConfig: state.company_config,
	fetching: state.troubleshooting_guides.fetching,
	troubleshootingGuide: state.troubleshooting_guides.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getTroubleshootingGuide: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? troubleshootingGuidesRestCrudThunksForSupplier.readOne(id)
				: troubleshootingGuidesRestCrudThunksForBuyer.readOne(id)
		),
	deleteGuide: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? troubleshootingGuidesRestCrudThunksForSupplier.delete(entity, 'id')
				: troubleshootingGuidesRestCrudThunksForBuyer.delete(entity, 'id')
		),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(TroubleshootingDetailPage))
);
